import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { SharedModule } from 'src/app/private/shared/shared.module';
import { PackageService } from 'src/app/private/services/package.service';
import { ClientService } from 'src/app/private/services/client.service';
import { CalendarModule } from 'primeng/calendar';

@Component({
  selector: 'app-assign-package',
  standalone: true,
  imports: [SharedModule, CalendarModule],
  templateUrl: './assign-package.component.html',
  styleUrl: './assign-package.component.scss'
})
export class AssignPackageComponent {

  @Input() visible: boolean = false;
  @Input() clientPackage: any = {};
  @Input() formAction: string = 'Assign';

  @Output() close = new EventEmitter<void>();
  @Output() reload = new EventEmitter<void>();

  assignForm!: FormGroup;
  isLoading = false;
  isOnboardExistingClient = false;

  clients: any[] = [];
  packages: any[] = [];

  billingCycles = [
    { label: 'Monthly', value: 'Monthly' },
    { label: 'Yearly', value: 'Yearly' },
    
  ];

  statusOptions = [
    { label: 'Active', value: 'Active' },
    { label: 'Inactive', value: 'Inactive' },
    { label: 'Paused', value: 'Paused' },
    { label: 'Suspended', value: 'Suspended' },
    { label: 'Cancelled', value: 'Cancelled' }
  ];

  paymentStatus = [
    { label: 'Pending', value: 'Pending' },
    { label: 'Paid', value: 'Paid' },
    { label: 'Failed', value: 'Failed' },
    { label: 'Refunded', value: 'Refunded' },
    { label: 'Cancelled', value: 'Cancelled' }
  ];

  constructor(
    private fb: FormBuilder,
    private packageService: PackageService,
    private clientService: ClientService,
    private messageService: MessageService
  ) {}

  /* ---------------------------------------
   * INIT
   --------------------------------------- */
  ngOnInit() {
    this.initForm();
    this.loadClients();
    this.loadPackages();
    this.listenPackageChange();
    this.billingModeListener();
    this.trialListener();
    this.billingTypeListener();

    if (this.clientPackage?.id !== undefined) {
      this.patchFormForEdit();
    }
  }

  /* ---------------------------------------
   * FORM INIT
   --------------------------------------- */
  initForm() {
    const today = new Date();

    this.assignForm = this.fb.group({
      client_id: ['', Validators.required],
      package_id: ['', Validators.required],
      quantity: [1, [Validators.required, Validators.min(1)]],
      status: ['Inactive', Validators.required],
       // 🔑 NEW
       billing_type: ['Recurring', Validators.required], // 'fixed' | 'Recurring'
      // RADIO MODE
      billing_mode: [null, Validators.required], // 'invoice' | 'renew'

      billing_cycle: [{ value: 'Monthly', disabled: true }],
      billing_occurrences: [{ value: 12, disabled: true }],

      current_period_start: [today, Validators.required],
      current_period_end: [{ value: null, disabled: false }],
      payment_status:['Pending'],
      // TRIAL
      is_trial: [false],
      trial_start: [{ value: null, disabled: true }],
      trial_end: [{ value: null, disabled: true }],

      cancel_at_period_end: [false],
      grace_period_days: [0],
      reminder_before_days: [7]
    });
    this.billingTypeListener()
  }

  billingTypeListener() {
  if(this.assignForm.get('billing_type')?.value  === 'Recurring') {
     this.assignForm.patchValue({
        billing_mode: 'Invoice'
      });
  }
  this.assignForm.get('billing_type')?.valueChanges.subscribe(type => {

    if (type === 'Recurring') {
      
      // ✅ Auto-select Invoice by default
      this.assignForm.patchValue({
        billing_mode: 'Invoice'
      });

      // Ensure billing fields are enabled
      this.assignForm.get('billing_cycle')?.enable();
      this.assignForm.get('billing_occurrences')?.enable();

      // Auto-calc dates
      this.updateBillingDates();

    } else {
      // ❌ Fixed billing → clear automation
      this.assignForm.patchValue({
        billing_mode: null
      });
    }
  });
}
  /* ---------------------------------------
   * BILLING MODE LISTENER (RADIO)
   --------------------------------------- */
  billingModeListener() {
  this.assignForm.get('billing_mode')?.valueChanges.subscribe(() => {
    // Billing fields are always enabled unless trial is ON
    if (!this.assignForm.value.is_trial) {
      this.assignForm.get('billing_cycle')?.enable();
      this.assignForm.get('billing_occurrences')?.enable();
      this.updateBillingDates();
    }
  });
}

  /* ---------------------------------------
   * TRIAL LOGIC
   --------------------------------------- */
  trialListener() {
  this.assignForm.get('is_trial')?.valueChanges.subscribe(isTrial => {

    if (isTrial) {
      // Enable trial dates
      this.assignForm.get('trial_start')?.enable();
      this.assignForm.get('trial_end')?.enable();

      // Disable billing during trial
      this.assignForm.get('current_period_start')?.disable();
      this.assignForm.get('current_period_end')?.disable();
      this.assignForm.get('billing_cycle')?.disable();
      this.assignForm.get('billing_occurrences')?.disable();

    } else {
      // Disable trial dates
      this.assignForm.get('trial_start')?.disable();
      this.assignForm.get('trial_end')?.disable();

      // Enable billing fields always
      this.assignForm.get('current_period_start')?.enable();
      this.assignForm.get('current_period_end')?.enable();
      this.assignForm.get('billing_cycle')?.enable();
      this.assignForm.get('billing_occurrences')?.enable();
    }
  });
}

  /* ---------------------------------------
   * PACKAGE CHANGE
   --------------------------------------- */
  listenPackageChange() {
    this.assignForm.get('package_id')?.valueChanges.subscribe(packageId => {
      const pkg = this.packages.find(p => p.package_id === packageId);
      if (!pkg) return;

      const start = new Date();
      const end = new Date();
      end.setDate(end.getDate() + (pkg.validity || 30));

      this.assignForm.patchValue({
        current_period_start: start,
        current_period_end: null
      });
    });
  }

  /* ---------------------------------------
   * UPDATE BILLING DATES
   --------------------------------------- */
  updateBillingDates() {
  if (this.assignForm.value.billing_type !== 'Recurring') {
    return; // Fixed billing → manual end date
  }

  const start = this.assignForm.value.current_period_start;
  if (!start) return;

  const cycle = this.assignForm.value.billing_cycle;
  const end = new Date(start);

  let occurrences = 1;

  switch (cycle) {
    case 'Monthly':
      end.setMonth(end.getMonth() + 12);
      occurrences = 12;
      break;

    case 'Yearly':
      end.setFullYear(end.getFullYear() + 1);
      occurrences = 1;
      break;
  }

  this.assignForm.patchValue({
    current_period_end: null,
    billing_occurrences: occurrences
  }, { emitEvent: false });
}

  /* ---------------------------------------
   * PATCH EDIT MODE
   --------------------------------------- */
  patchFormForEdit() {
    const data = this.clientPackage;
    if (!data) return;

    this.assignForm.patchValue({
      client_id: data.subscription?.client_id,
      package_id: data.package_id,
      quantity: data.quantity ?? 1,
      status: data.status ?? 'Inactive',
      billing_type: data.billing_type ?? null,
      payment_status: data.payment_status ?? null,
      billing_mode: data.billing_mode ?? null,
      billing_cycle: data.billing_cycle ?? 'Monthly',
      cancel_at_period_end: data.cancel_at_period_end ?? false,
      grace_period_days: data.grace_period_days ?? 0,
      reminder_before_days: data.reminder_before_days ?? 7
    });

    if (data.current_period_start) {
      this.assignForm.patchValue({ current_period_start: new Date(data.current_period_start) });
    }
    if (data.current_period_end) {
      this.assignForm.patchValue({ current_period_end: new Date(data.current_period_end) });
    }

    if (data.billing_mode === 'Renew' || data.billing_mode === 'Fixed' ) {
      this.assignForm.get('billing_cycle')?.enable();
      this.assignForm.get('billing_occurrences')?.enable();
    }

    if (data.is_trial) {
      this.assignForm.patchValue({ is_trial: true });
      this.assignForm.get('trial_start')?.enable();
      this.assignForm.get('trial_end')?.enable();

      if (data.trial_start) {
        this.assignForm.patchValue({ trial_start: new Date(data.trial_start) });
      }
      if (data.trial_end) {
        this.assignForm.patchValue({ trial_end: new Date(data.trial_end) });
      }
    }
  }

  /* ---------------------------------------
   * LOADERS
   --------------------------------------- */
  loadClients() {
    this.clientService.getAllClients({
      page: 0,
      limit: 1000,
      where: { status: 'active' },
      order: { col: '"client"."name"', dir: 'ASC' }
    }).subscribe(res => this.clients = res.rows || []);
  }

  loadPackages() {
    this.packageService.getAllPackage({
      page: 0,
      limit: 1000,
      where: { status: 'Active' },
      order: { col: '"package"."package_name"', dir: 'ASC' }
    }).subscribe(res => this.packages = res.rows || []);
  }

  /* ---------------------------------------
   * ONBOARD EXISTING CLIENT TOGGLE
   --------------------------------------- */
  onOnboardToggle() {
    const fieldsToToggle = [
      'status', 'payment_status', 'billing_type', 'billing_mode',
      'billing_cycle', 'billing_occurrences', 'current_period_start',
      'current_period_end', 'is_trial', 'trial_start', 'trial_end',
      'cancel_at_period_end', 'grace_period_days', 'reminder_before_days', 'quantity'
    ];

    if (this.isOnboardExistingClient) {
      fieldsToToggle.forEach(f => this.assignForm.get(f)?.disable());
    } else {
      fieldsToToggle.forEach(f => this.assignForm.get(f)?.enable());
      // Re-apply original disabled states
      this.assignForm.get('billing_cycle')?.disable();
      this.assignForm.get('billing_occurrences')?.disable();
      this.assignForm.get('trial_start')?.disable();
      this.assignForm.get('trial_end')?.disable();
    }
  }

  /* ---------------------------------------
   * SUBMIT
   --------------------------------------- */
  submitForm() {
    const clientId = this.assignForm.get('client_id')?.value;
    const packageId = this.assignForm.get('package_id')?.value;

    if (!clientId || !packageId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please select a Client and a Package.'
      });
      return;
    }

    // --- ONBOARD EXISTING CLIENT MODE ---
    if (this.isOnboardExistingClient) {
      this.isLoading = true;
      this.packageService.onboardExistingClient({
        client_id: clientId,
        package_id: packageId
      }).subscribe({
        next: (res: any) => {
          this.isLoading = false;
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: res?.message || 'Client onboarded successfully!'
          });
          this.reload.emit();
          this.handleClose();
        },
        error: (err: any) => {
          this.isLoading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: err?.error?.message || 'Onboarding failed.'
          });
        }
      });
      return;
    }

    // --- NORMAL FLOW ---
    if (this.assignForm.invalid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please fill all required fields.'
      });
      return;
    }

    this.isLoading = true;
    const payload = this.formatPayload(this.assignForm.getRawValue());
    console.log(payload)
    const api$ = this.clientPackage?.id
      ? this.packageService.updatePackageSubscription({ ...payload, id: this.clientPackage.id })
      : this.packageService.createPackageSubscription(payload);

    api$.subscribe({
      next: () => {
        this.isLoading = false;
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Subscription saved successfully!' });
        this.reload.emit();
        this.handleClose();
      },
      error: () => {
        this.isLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Operation failed.' });
      }
    });
  }

  formatPayload(payload: any) {
    const convert = (d: any) => d instanceof Date ? d.toISOString() : d;
    return {
      ...payload,
      current_period_start: convert(payload.current_period_start),
      current_period_end: convert(payload.current_period_end),
      trial_start: convert(payload.trial_start),
      trial_end: convert(payload.trial_end)
    };
  }

  /* ---------------------------------------
 * PACKAGE SELECT EVENT (HTML COMPAT FIX)
 ---------------------------------------- */
onPackageSelect(event: any) {
  const packageId = event.value;
  const pkg = this.packages.find(p => p.package_id === packageId);
  if (!pkg) return;

  const start = new Date();
  const end = new Date();

  // Apply package validity
  end.setDate(end.getDate() + Number(pkg.validity || 30));

  this.assignForm.patchValue({
    current_period_start: start,
    current_period_end: null
  });

  // If Auto Renew selected → recalc billing
  if (this.assignForm.value.billing_mode === 'Invoice' || this.assignForm.value.billing_mode === 'Renew' ) {
    this.updateBillingDates();
  }
}

  handleClose() {
    this.assignForm.reset();
    this.close.emit();
  }
}
